import { DataGrid, DataGridColumn, DataGridRef } from '@cimplico/components';
import { Ref, useState } from 'react';
import { ROW_SIZES } from '../../constants';
import { useWorkpapers } from '../../queries/use-workpapers';
import { Workpaper, WorkpaperStatusEnum } from '../../types/workpaper';
import WorkpaperRow from './WorkpaperRow';
import EmptyStateNew from '../../components/empty-state/EmptyStateNew';
import NoResults from '../../components/empty-state/NoResults';
import { DocumentCheckIcon } from '@heroicons/react/24/outline';
import { useIsFirmRole } from '../../hooks/use-is-firm-role';
import { useUserContext } from '../../providers/UserProvider';

const columns: DataGridColumn[] = [
    {
        header: 'Workpaper',
        alignment: 'left',
        width: '1.5fr',
    },
    {
        header: 'Entity',
        alignment: 'left',
        width: '1.5fr',
    },
    {
        header: 'Last Updated',
        alignment: 'left',
        width: '1.5fr',
    },
    {
        header: 'Assigned',
        alignment: 'center',
        width: '0.5fr',
    },
    {
        header: 'Due',
        alignment: 'center',
        width: '1.5fr',
    },
    {
        header: 'Progress',
        alignment: 'center',
        width: '1fr',
    },
];

type WorkpapersTableProps = {
    search?: string;
    sort?: string;
    filter?: string;
    onSelect?: (workpapers: Workpaper[]) => void;
    tableRef?: Ref<DataGridRef> | null;
    emptyStateNavigateTo?: string;
    disableCreateWorkpaper?: boolean;
    disableCreateWorkpaperHint?: string;
    tabIndex?: number;
};

function WorkpapersTable({
    emptyStateNavigateTo,
    search = '',
    sort = '',
    filter = '',
    onSelect,
    tableRef,
    disableCreateWorkpaper = false,
    disableCreateWorkpaperHint,
    tabIndex = 0,
}: WorkpapersTableProps): JSX.Element {
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(ROW_SIZES[0]);

    const { isExternalUser } = useIsFirmRole();
    const { id: userId } = useUserContext();
    const status =
        tabIndex === 0
            ? undefined
            : tabIndex === 1
            ? WorkpaperStatusEnum.ACTIVE
            : tabIndex === 2
            ? WorkpaperStatusEnum.COMPLETE
            : tabIndex === 4
            ? WorkpaperStatusEnum.ARCHIVED
            : undefined;
    const workpapersQuery = useWorkpapers({ page, limit, search, sort, filter, status, assigned: tabIndex === 3 ? userId : undefined });

    function handleSelect(ids: string[]): void {
        if (onSelect) {
            const workpapers = workpapersQuery.data?.workpapers || [];
            onSelect(workpapers.filter((wp) => ids.includes(wp.id)));
        }
    }

    if (workpapersQuery.isLoading) {
        return <></>;
    }
    if (workpapersQuery.isError || !workpapersQuery.isSuccess) {
        return <p>Error!</p>;
    }

    const showEmptyNew = !search;

    const { workpapers, totalItems } = workpapersQuery.data;

    return (
        <div className="relative">
            <DataGrid
                columns={columns}
                rowSizes={ROW_SIZES}
                totalRows={totalItems}
                onPageChange={setPage}
                onRowSizeChange={setLimit}
                isLoading={workpapersQuery.isFetching}
                onSelect={handleSelect}
                ref={tableRef}
                emptyMessage={
                    showEmptyNew ? (
                        <EmptyStateNew
                            icon={<DocumentCheckIcon />}
                            navigateTo={emptyStateNavigateTo || '/workpapers/new'}
                            title="Add your first Workpaper"
                            actionText="New Workpaper"
                            disableActionButtonHint={disableCreateWorkpaperHint}
                            disableActionButton={disableCreateWorkpaper || isExternalUser}
                            helpLink="https://learn.cimplico.com/docs/creating-workpapers"
                            helpText="Leverage several built-in features to ensure accuracy and governance around the evidencing process."
                        />
                    ) : (
                        <NoResults search={search} />
                    )
                }
            >
                {workpapers.map((workpaper) => {
                    return <WorkpaperRow id={workpaper.id} key={workpaper.id} workpaper={workpaper} />;
                })}
            </DataGrid>{' '}
        </div>
    );
}

export default WorkpapersTable;
